import dayjs from "@punchup/utils/dayjs";
import { z } from "zod";

/**
 * Login-as (impersonation) markers: what both server and browser code need.
 * Signing and cookie writes are in `./impersonation-server`, so the browser
 * bundle (the banner) never ships them.
 *
 * Login-as replaces a super admin's Supabase session with the target user's.
 * These two cookies are the only record in the browser that the session is
 * borrowed:
 *
 * - IMPERSONATION_COOKIE (httpOnly) holds who is impersonating whom, and since
 *   when. It is signed (HMAC-SHA256) because the audit log trusts its admin and
 *   target ids: anyone can write a cookie in their own browser, and an unsigned
 *   one could log `login_as_end` under any admin. Only `login-as` can sign one.
 *   It holds no admin credential on purpose: the browser runs as the target, so
 *   any script the target's content can run there could use one. Ending an
 *   impersonation signs out, and the admin signs in again.
 * - IMPERSONATION_BANNER_COOKIE (readable by the browser) holds only what the
 *   banner shows. Tampering with it changes nothing but the banner.
 *
 * Both names carry the `__Host-` prefix: the browser then refuses a cookie of
 * that name from a sibling subdomain (Domain=punchup.live), which could
 * otherwise shadow the real marker and remove the time limit. The prefix
 * requires Secure and Path=/ on every set and delete.
 *
 * Edge-safe: middleware imports these modules, so they stay free of Node APIs
 * and Supabase clients.
 */
export const IMPERSONATION_COOKIE = "__Host-pu_impersonation";
export const IMPERSONATION_BANNER_COOKIE = "__Host-pu_impersonation_banner";

export const IMPERSONATION_RETURN_PATH = "/api/v2/internal/login-as/return";

// After this, middleware and the banner end the impersonation.
export const IMPERSONATION_MAX_MINUTES = 60;

// Matches the @supabase/ssr auth cookie lifetime. The markers must outlive the
// borrowed session they mark: if they expired first, a browser reopened days
// later would still be signed in as the target, with no banner and no end.
export const IMPERSONATION_COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;

const isoTimestamp = z
  .string()
  .refine((value) => dayjs(value).isValid(), "Invalid timestamp");

export const ImpersonationBannerStateSchema = z.object({
  targetUserId: z.string().min(1),
  startedAt: isoTimestamp, // UTC
});

export const ImpersonationStateSchema = ImpersonationBannerStateSchema.extend({
  adminUserId: z.string().min(1),
  adminEmail: z.string(),
  // The target session's id (the access token's `session_id` claim): the key
  // of its row in public.impersonation_sessions.
  sessionId: z.string().min(1),
});

export type ImpersonationBannerState = z.infer<
  typeof ImpersonationBannerStateSchema
>;
export type ImpersonationState = z.infer<typeof ImpersonationStateSchema>;

// `replaced`: a new login-as overwrote a marker that was still valid.
export type ImpersonationEndReason =
  | "returned"
  | "expired"
  | "signed_out"
  | "replaced";

export function parseJson<T>(schema: z.ZodType<T>, json: string): T | null {
  try {
    const result = schema.safeParse(JSON.parse(json));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export const parseImpersonationBannerCookie = (value: string | undefined) =>
  value ? parseJson(ImpersonationBannerStateSchema, value) : null;

export function getImpersonationElapsedMinutes(
  startedAt: string,
  now = dayjs()
): number {
  return Math.max(0, now.diff(dayjs(startedAt), "minute"));
}

/**
 * The time-limit rule, on minutes from any clock: the banner counts on the
 * server's clock, middleware and the return route on their own.
 */
export function isPastImpersonationLimit(elapsedMinutes: number): boolean {
  return elapsedMinutes >= IMPERSONATION_MAX_MINUTES;
}

export function isImpersonationExpired(
  startedAt: string,
  now = dayjs()
): boolean {
  return isPastImpersonationLimit(
    getImpersonationElapsedMinutes(startedAt, now)
  );
}

/**
 * Whether a Supabase auth error says nothing about the session: a network
 * failure or a server error. Such an error is not proof of sign-out, so
 * middleware and the return route keep the markers and decide later. Any other
 * error (no session, revoked or unknown session) means the session is gone.
 */
export function isTransientAuthError(
  error: { name?: string; status?: number } | null | undefined
): boolean {
  if (!error) return false;
  return (
    error.name === "AuthRetryableFetchError" ||
    error.status === undefined ||
    error.status === 0 ||
    error.status >= 500
  );
}
